import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from './NotificationPanel';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['data-management']); // Expand Data Management by default
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { unreadCount } = useNotifications();

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('winglet_dev_mode');
      await signOut?.();
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      window.location.href = '/login';
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const navItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'Gauge',
      tooltip: 'Overview and recent activity'
    },
    {
      label: 'Current Trip',
      path: '/current-trip-view',
      icon: 'Radio',
      tooltip: 'Live flight duty monitoring & FAR 117 compliance'
    },
    {
      label: 'Calendar',
      path: '/calendar',
      icon: 'Calendar',
      tooltip: 'Rotation calendar view'
    },
    {
      label: 'Data Management',
      path: '/data-management',
      icon: 'Database',
      tooltip: 'Manage rotations and timecards'
    },
    {
      label: 'Share My Flights',
      path: '/share-my-flights',
      icon: 'Share2',
      tooltip: 'Keep family in the loop - set it and forget it'
    },
    {
      label: 'Reroute Lab',
      path: '/reroute-lab',
      icon: 'GitBranch',
      tooltip: 'Test reroute detection and §23L pay (hours-only)'
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      tooltip: 'Configuration and preferences'
    }
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={toggleSidebar}
        className="mobile-menu-button"
        aria-label="Toggle menu"
      >
        <Icon name={isOpen ? 'X' : 'Menu'} size={24} color="var(--color-accent-cyan)" />
      </button>
      {/* Overlay */}
      {isOpen && (
        <div
          className="overlay lg:hidden"
          onClick={closeSidebar}
        />
      )}
      {/* Desktop Sidebar */}
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isOpen ? 'open' : ''}`}>
        {/* Header with Logo and Collapse Button */}
        <div className="sidebar-header">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-cyan to-accent-blue flex items-center justify-center flex-shrink-0">
              <Icon name="Plane" size={20} color="#ffffff" />
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-heading font-bold text-foreground truncate">Winglet</h1>
                <p className="text-xs text-muted-foreground truncate">Flight Crew Portal</p>
              </div>
            )}
          </div>

          {/* Collapse Button */}
          <button
            onClick={toggleCollapse}
            className="p-2 hover:bg-tertiary rounded-lg transition-colors flex-shrink-0"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon 
              name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} 
              size={20} 
              color="var(--color-muted-foreground)" 
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems?.map((item) => (
            <NavLink
              key={item?.path}
              to={item?.path}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
              title={isCollapsed ? item?.label : ''}
            >
              <Icon name={item?.icon} size={20} className="nav-item-icon" />
              <span className="nav-item-label">{item?.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Notification Bell */}
        <div className="border-t border-border p-2">
          <button
            className="relative p-2 hover:bg-tertiary rounded-lg transition-colors w-full flex items-center justify-center"
            title="Notifications"
            onClick={() => setIsNotificationPanelOpen(true)}
          >
            <Icon name="Bell" size={20} color="var(--color-accent-cyan)" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Theme Toggle Button */}
        <div className="mt-auto p-4 border-t border-border">
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 w-full px-4 h-12 rounded-xl text-sm font-medium text-muted-foreground hover:bg-tertiary hover:text-foreground transition-all duration-250"
            aria-label="Toggle theme"
            title={isCollapsed ? (theme === 'dark' ? 'Light Mode' : 'Dark Mode') : ''}
          >
            <Icon 
              name={theme === 'dark' ? 'Sun' : 'Moon'} 
              size={20} 
              className="nav-item-icon" 
            />
            <span className="nav-item-label">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          
          {/* Login/Logout Button */}
          {user ? (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-4 h-12 rounded-xl text-sm font-medium text-muted-foreground hover:bg-error/10 hover:text-error transition-all duration-250 mt-2"
              aria-label="Sign out"
              title={isCollapsed ? 'Sign Out' : ''}
            >
              <Icon name="LogOut" size={20} className="nav-item-icon" />
              <span className="nav-item-label">Sign Out</span>
            </button>
          ) : (
            <button
              onClick={handleSignIn}
              className="flex items-center gap-3 w-full px-4 h-12 rounded-xl text-sm font-medium text-muted-foreground hover:bg-success/10 hover:text-success transition-all duration-250 mt-2"
              aria-label="Sign in"
              title={isCollapsed ? 'Sign In' : ''}
            >
              <Icon name="LogIn" size={20} className="nav-item-icon" />
              <span className="nav-item-label">Sign In</span>
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Navigation */}
      <nav className="mobile-nav lg:hidden">
        {navItems?.slice(0, 4)?.map((item) => (
          <NavLink
            key={item?.path}
            to={item?.path}
            className={({ isActive }) =>
              `mobile-nav-item ${isActive ? 'active' : ''}`
            }
          >
            <Icon name={item?.icon} size={24} className="mobile-nav-icon" />
            <span>{item?.label}</span>
          </NavLink>
        ))}
        <button
          onClick={toggleTheme}
          className="mobile-nav-item"
          aria-label="Toggle theme"
        >
          <Icon 
            name={theme === 'dark' ? 'Sun' : 'Moon'} 
            size={24} 
            className="mobile-nav-icon" 
          />
          <span>Theme</span>
        </button>
      </nav>

      {/* Notification Panel */}
      <NotificationPanel 
        isOpen={isNotificationPanelOpen} 
        onClose={() => setIsNotificationPanelOpen(false)} 
      />
    </>
  );
};

export default Sidebar;